import { createElement } from 'react';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { ReactKeyboardEvent, ReactMouseEvent } from '../../../react-synthetic-events';
import { setAlpha } from '../../../design-tokens';
import { postfixTestId } from '../../../util/testId';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { useColorChipA11yInfo } from './hooks/useColorChipA11yInfo';
import { EMPTY_CHIP_COLOR } from '.';
import { ColorChipsProps } from './types';

import cx from 'classnames';
import cn from './ColorChips.css.json';
import './ColorChips.css';

export const ColorChips = (props: ColorChipsProps) => {
  const localize = useNCTextLocalizer('ColorPicker');
  let styleClassName;
  if (props.type === 'nocolor') {
    styleClassName = cn.noColor;
  } else if (props.type === 'automatic') {
    styleClassName = cn.automatic;
  } else if (props.color === EMPTY_CHIP_COLOR) {
    styleClassName = cn.emptyChip;
  } else if (props.type === 'custom') {
    styleClassName = cn.customColorchip;
  } else {
    styleClassName = cn.colorchip;
  }

  const dt = useDesignTokens();
  const tokens = {
    bd: 'transparent',
    'hover-bd': setAlpha(dt.c.colorUiBorder, 0.5),
    'selected-bd': dt.c.colorUiBorder,
    'empty-bg': 'transparent',
    'inner-bd': dt.c.colorUiBorder,
    'no-color-mark': dt.c.colorAlert,
    padding: dt.spacing.core.xxs,
  };

  const styles = renderInlineCssVars(tokens, 'colorchip');
  const shouldRenderAutomaticSwatch = props.type === 'automatic' && !!props.renderAutomaticSwatch;
  const tabIndex =
    ['automatic', 'nocolor'].includes(props.type) || props.selected || props.focusable ? 0 : -1;

  const { ariaLabel, title, ariaHidden } = useColorChipA11yInfo({
    color: props.color,
    type: props.type,
  });

  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      props.onPress?.(props.color, props.type);
    }
  };

  const onClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (props.color !== EMPTY_CHIP_COLOR && props.onPress) {
      shouldRenderAutomaticSwatch
        ? props.onPress('#000000', 'automatic')
        : props.onPress(props.color, props.type);
    }
  };

  const MultiColorSwatchComponent = props.renderAutomaticSwatch
    ? props.renderAutomaticSwatch({
        getAttributes() {
          let multiColorArialabel = `${localize('automatic.label')}, ${localize(
            'multipleSmallCaseColors.label'
          )}`;
          if (props.selected) {
            multiColorArialabel = localize('basicTab.selected.aria', {
              ariaLabel: multiColorArialabel,
            });
          }
          const multiColorTitle = localize('multipleColors.label');
          return {
            ariaLabel: multiColorArialabel,
            title: multiColorTitle,
          };
        },
      })
    : null;

  return (
    <div
      role="button"
      tabIndex={tabIndex}
      style={styles}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-hidden={ariaHidden}
      data-testid={postfixTestId(props.testId, `${props.type}-${props.color}`)}
      className={cx(styleClassName, props.selected && !props.tabToStandardBlock && cn.selected)}
      {...(!shouldRenderAutomaticSwatch && {
        title,
        'aria-label': ariaLabel
          ? props.selected
            ? localize('basicTab.selected.aria', { ariaLabel })
            : ariaLabel
          : undefined,
      })}
    >
      {shouldRenderAutomaticSwatch ? (
        MultiColorSwatchComponent
      ) : (
        <span
          className={cx(
            `${styleClassName}-inner`,
            ((props.color === '#FFFFFF' && dt.themeVariant !== 'high-contrast') ||
              (props.color === '#000000' && dt.themeVariant === 'high-contrast')) &&
              cn.visibleInnerBorder
          )}
          style={{ backgroundColor: props.color, ...props.style }}
        />
      )}
    </div>
  );
};
